/**
 * MessagesPage.js — Unified internal communication surface.
 *
 * Phase 1 of the Secure Chat + Secure Email consolidation. Collapses
 * two previously separate sidebar entries into one tabbed surface.
 *
 * Architecture: this page is a thin shell — each tab embeds the existing
 * page component (ChatPage, EmailPage). Zero changes to the underlying
 * IPC handlers, zero schema risk, and the real-time Socket.io connection
 * inside ChatPage continues to work exactly as before (each tab switch
 * causes a brief reconnect; Socket.io's auto-reconnect handles it).
 *
 * Why one surface? The encryption model is identical:
 *   • Both use TOTP_ENCRYPTION_KEY for AES-256-GCM at rest.
 *   • Both have HIPAA §164.312(a)(2)(iv) audit logging at the IPC layer.
 *   • The functional difference is conversational style — chat is short
 *     and real-time; email is subject-line + thread. The crypto and
 *     security posture are the same.
 *
 * Internal vs external send: the External Email path (email:send-external
 * via SMTP) is preserved as-is inside the Email tab. It's the one place
 * where data legitimately leaves the BAA boundary, and EmailPage already
 * warns the user about that. We don't try to disguise it as "chat";
 * external delivery deserves a deliberate, visible UI affordance.
 *
 * Phase 2 (deferred, separate PR): unified composer that picks the
 * backend (chat:messages:send vs email:send vs email:send-external)
 * based on recipient type and whether a subject is provided. That's a
 * bigger change touching three IPC contracts and is best tackled when
 * the UI patterns settle.
 */
import React, { useState } from 'react';
import { MessageSquare, Mail, Lock } from 'lucide-react';
import ChatPage from './ChatPage';
import EmailPage from './EmailPage';

const TABS = [
  { id: 'chat',  label: 'Direct Messages & Channels', icon: MessageSquare, perm: 'chat:read'  },
  { id: 'email', label: 'Email Threads',              icon: Mail,          perm: 'email:read' },
];

export default function MessagesPage({ user, onNavigate, initialTab }) {
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const visibleTabs = TABS.filter((t) => perms.includes(t.perm));

  const defaultTab = visibleTabs.find((t) => t.id === initialTab)?.id
    || visibleTabs[0]?.id
    || 'chat';

  const [activeTab, setActiveTab] = useState(defaultTab);

  if (visibleTabs.length === 0) {
    return (
      <div className="page">
        <h1 className="page-title">Messages</h1>
        <div className="empty-state" style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
          <Lock size={48} />
          <p>You don't have permission to access chat or email.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page messages-page">
      <div className="messages-header">
        <h1 className="page-title">Messages</h1>
        <div className="messages-encryption-badge" title="Internal messages are encrypted at rest with AES-256-GCM (HIPAA §164.312(a)(2)(iv))">
          <Lock size={14} />
          <span>End-to-end encrypted internally</span>
        </div>
      </div>

      <div className="messages-tabs" role="tablist" aria-label="Messaging sections">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`messages-panel-${tab.id}`}
              className={`messages-tab ${isActive ? 'messages-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={18} strokeWidth={isActive ? 2.2 : 1.8} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div
        className="messages-panel"
        id={`messages-panel-${activeTab}`}
        role="tabpanel"
      >
        {activeTab === 'chat' && (
          <div className="messages-embedded">
            <ChatPage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'email' && (
          <div className="messages-embedded">
            <EmailPage user={user} onNavigate={onNavigate} />
          </div>
        )}
      </div>

      <style jsx>{`
        .messages-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 12px;
        }
        .messages-encryption-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          background: #ECFDF5;
          color: #065F46;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          border: 1px solid #A7F3D0;
        }
        .messages-tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid #E8ECF1;
          margin-bottom: 16px;
          overflow-x: auto;
        }
        .messages-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border: none;
          background: transparent;
          color: #6C757D;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          white-space: nowrap;
          transition: color 0.15s, border-color 0.15s;
        }
        .messages-tab:hover {
          color: #2E8B98;
        }
        .messages-tab--active {
          color: #2E8B98;
          border-bottom-color: #2E8B98;
          font-weight: 600;
        }
        /* Suppress the inner page's outer padding and title — the shell
           provides its own header so we avoid double-stacking. */
        .messages-embedded :global(.page) {
          padding: 0;
          background: transparent;
        }
        .messages-embedded :global(.page-title) {
          display: none;
        }
      `}</style>
    </div>
  );
}
